import { css } from '@emotion/css';
import { ActionIcon, Group, Stack, Text, useMantineTheme } from '@mantine/core';
import { IconCircle, IconCircleCheckFilled } from '@tabler/icons-react';
import type { TaskActionType } from 'app/Private/Clients/Detail/TaskTab/types/task-action-type';
import { useHistory } from 'react-router';
import type { Task } from 'shared/hooks/use-load-tasks';
import { useUpdateTaskStatus } from 'shared/hooks/use-update-task-status';
import { TaskActions } from './TaskActions';
import { TaskBadges } from './TaskBadges';

type TaskItemProps = {
  task: Task;
};

export const TaskItem = ({ task }: TaskItemProps) => {
  const theme = useMantineTheme();
  const history = useHistory();

  const { updateTaskStatus, isUpdateTaskStatusLoading } = useUpdateTaskStatus();

  const handleToggleComplete = () => {
    const actionType: TaskActionType = task.completed ? 'revert' : 'complete';

    updateTaskStatus({ task_ids: [String(task.id)], action_type: actionType });
  };

  const handleOpenTask = () => history.push(`/tasks/${task.id}`);

  return (
    <Stack className={styles.hovered}>
      <Group align="start" gap="sm" py="md" px="sm" wrap="nowrap">
        <ActionIcon
          onClick={handleToggleComplete}
          variant="transparent"
          loading={isUpdateTaskStatusLoading}
          disabled={isUpdateTaskStatusLoading}
        >
          {task.completed ? (
            <IconCircleCheckFilled size={24} stroke={1.5} color={theme.colors.purple[6]} />
          ) : (
            <IconCircle size={24} stroke={1.5} color={theme.colors.gray[5]} />
          )}
        </ActionIcon>
        <Group w="100%" align="start" justify="space-between" onClick={handleOpenTask}>
          <Stack gap={4}>
            <Text
              td={task.completed ? 'line-through' : 'none'}
              c={task.completed ? 'dimmed' : 'dark'}
            >
              {task.title}
            </Text>
            <TaskBadges task={task} />
          </Stack>
          <TaskActions task={task} />
        </Group>
      </Group>
    </Stack>
  );
};

const styles = {
  hovered: css`
    &:not(:last-child) {
      border-bottom: 1px solid var(--mantine-color-gray-3);
    }

    > div {
      transition: background-color 0.15s;
      cursor: pointer;
      background-color: transparent;
    }

    &:hover > div {
      background-color: var(--mantine-color-purple-1);
    }

    #commet-box {
      transition: background-color 0.15s;
    }

    &:hover #comment-box {
      background-color: var(--mantine-color-purple-2) !important;
    }
  `,
};
